import React, { useState } from 'react';

interface FloatingLabelInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  touched?: boolean;
  isPlaceholder?: boolean;
  containerClassName?: string;
  labelClassName?: string;
  variant?: 'default' | 'compact';
  showPlaceholder?: boolean;
  onCustomFocus?: (name?: string) => void;
  onCustomBlur?: (name?: string) => void;
}

export const FloatingLabelInput = React.forwardRef<HTMLInputElement, FloatingLabelInputProps>(
  ({ 
    label, 
    error, 
    touched, 
    isPlaceholder,
    className = '', 
    containerClassName = '',
    labelClassName = '',
    variant = 'default',
    showPlaceholder = true,
    onCustomFocus,
    onCustomBlur,
    onFocus,
    onBlur,
    id,
    name,
    placeholder,
    disabled,
    ...props 
  }, ref) => {
    const [isErrorVisible, setIsErrorVisible] = useState(Boolean(touched && error));

    React.useEffect(() => {
      setIsErrorVisible(Boolean(touched && error));
    }, [touched, error]);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      onFocus?.(e);
      onCustomFocus?.(name);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(e);
      onCustomBlur?.(name);
    };

    const heightClass = variant === 'compact' ? 'h-[42px]' : 'h-[62px]';
    const paddingClass = variant === 'compact' ? 'pt-[22px] pb-[6px]' : 'pt-[32px] pb-[8px]';
    const labelTopClass = variant === 'compact' ? 'top-[16px] peer-focus:top-[8px] peer-[:not(:placeholder-shown)]:top-[8px]' : 'top-[20px] peer-focus:top-[12px] peer-[:not(:placeholder-shown)]:top-[12px]';
    
    const containerMarginClass = touched && error ? 'mb-6' : '';

    return (
      <div className={`relative ${containerClassName} ${containerMarginClass}`}>
        <input
          ref={ref}
          id={id || name}
          name={name}
          placeholder={showPlaceholder ? placeholder : ''}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          className={`peer block w-full rounded-xl border-0 bg-gray-50/50 shadow-sm ring-1 ring-inset ${heightClass} ${
            !touched || !error
              ? 'ring-gray-200 focus:ring-2 focus:ring-primary-500'
              : 'ring-red-300 focus:ring-2 focus:ring-red-500'
          } text-gray-900 placeholder:text-transparent focus:placeholder:text-gray-400 sm:leading-6 px-4 ${paddingClass} ${
            isPlaceholder ? 'text-gray-400 italic' : ''
          } ${disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''} ${className}`}
          {...props}
        />
        <label
          htmlFor={id || name}
          className={`absolute left-4 text-base text-gray-500 transition-all duration-200 transform pointer-events-none
            peer-focus:text-xs peer-focus:text-gray-500
            peer-[:not(:placeholder-shown)]:text-xs peer-[:not(:placeholder-shown)]:text-gray-500
            ${labelTopClass} ${labelClassName} ${disabled ? 'text-gray-400' : ''}`}
        >
          {label}
        </label>
        
        <div 
          className={`absolute left-0 right-0 -bottom-6 transform transition-all duration-300 ease-in-out ${
            isErrorVisible 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 -translate-y-1 pointer-events-none'
          }`}
        >
          {touched && error && (
            <div className="flex items-center px-1">
              <span className="w-1 h-1 rounded-full bg-red-400 mr-1.5 opacity-80"></span>
              <span className="text-xs font-medium tracking-wide text-red-500">
                {error}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }
);

FloatingLabelInput.displayName = 'FloatingLabelInput'; 